/**
 * Générateur — À partir de dénombrements, il calcule des probabilités pour des
 * expériences aléatoires simples à une ou deux épreuves.
 * Attendu ID : 216 | Niveau : 3ème | Domaine : Probabilités (d15, O4)
 *
 * Source doc prof (OGD FONCTIONS 3e, séance B2S1 + Créés n°6-7). Plan de
 * cartes auto-validé 2026-07-16 (journal TODO_EXERCISEUR_3E.md §6) :
 *
 *   « loterie »      — dénombrement à catégories + effectif manquant (perdants)
 *   « urne »         — P(événement) et P(contraire) en fractions
 *   « contraire »    — énoncer l'événement contraire (QCM, phrase non négative)
 *   « tableau »      — tableau à double entrée + probabilité conditionnelle
 *                      en acte (« si l'élève est DP… » : l'univers change)
 *   « deuxepreuves » — roue à k secteurs, deux tours (1/k², (2k−1)/k²)
 *   « remise »       — deux tirages avec remise (la remise ne change rien)
 *   « roues »        — deux roues → nombres à deux chiffres (énumération)
 *   « doubles »      — deux urnes numérotées : P(double) (énumération)
 *
 * Toutes les probabilités en FRACTIONS IRRÉDUCTIBLES (2 trous num/den).
 */

import {
    randomChoice, randInt, shuffle, tex,
    makeQcm, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 216;

const L_LOTERIE = 'Calculer des probabilités par dénombrement (loterie)';
const L_URNE = 'Probabilité d\'un événement et de son contraire';
const L_CONTRAIRE = 'Énoncer un événement contraire';
const L_TABLEAU = 'Probabilités dans un tableau à double entrée';
const L_DEUXEPREUVES = 'Deux épreuves identiques (roue, pièce)';
const L_REMISE = 'Deux tirages avec remise';
const L_ROUES = 'Deux roues qui forment un nombre';
const L_DOUBLES = 'Deux urnes numérotées : obtenir un double';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const pgcd = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a || 1; };
const irr = (p, q) => { const g = pgcd(p, q); return [p / g, q / g]; };
// Fraction dessinée dont numérateur et dénominateur sont deux trous
// (convention triple-accolade, FORMATS_TANOED §0.5bis). À insérer dans une
// ligne de phrase entourée de `$…$`.
const trousFrac = (p, q) => { const [a, b] = irr(p, q); return `\\dfrac{{{${a}}}}{{{${b}}}}`; };

const T_TH = 'background:#1E3A8A;color:#fff;padding:5px 10px;border:1px solid #64748b;font-weight:600';
const T_TD = 'padding:5px 10px;border:1px solid #64748b;text-align:center';

// ═════════ « loterie » ═════════

const LOTERIES = [
    ['billets', 'une enceinte connectée', 'une grosse peluche', 'une petite peluche', 'un porte-clés'],
    ['tickets', 'un vélo', 'un ballon de foot', 'un livre', 'un stylo fantaisie'],
    ['enveloppes', 'une console', 'un jeu de société', 'un puzzle', 'un badge'],
];

const genLoterieTrous = () => {
    const [support, lot1, lot2, lot3, lot4] = randomChoice(LOTERIES);
    let N; let a; let b; let c; let d; let perd;
    for (let essai = 0; essai < 60; essai++) {
        N = randomChoice([120, 150, 180, 200]);
        a = randomChoice([2, 4, 5, 6, 8, 10]);
        b = randInt(1, 4) * 6; c = randInt(4, 9) * 6; d = randInt(8, 14) * 5;
        perd = N - a - b - c - d;
        if (perd >= 20 && perd <= N / 2) break;
        perd = 0;
    }
    if (!perd) { N = 180; a = 4; b = 12; c = 36; d = 68; perd = 60; } // repli (doc Ex.1)
    const phrase = `$P(\\text{gros lot}) = ${trousFrac(a, N)} \\qquad P(\\text{lot moyen}) = ${trousFrac(b + c, N)}`
        + ` \\qquad P(\\text{ne rien gagner}) = ${trousFrac(perd, N)}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Un sac contient ${N} ${support} : ${a} font gagner le GROS LOT, ${lot1} ; ${b} font gagner ${lot2}, `
        + `${c} font gagner ${lot3}, ${d} font gagner ${lot4} ; les autres sont perdants. On pioche au hasard.</p>`
        // Quels lots sont « moyens » est une DONNÉE de l'énoncé, pas un indice.
        + `<p>Les « lots moyens » sont ${lot2} et ${lot3}.</p>`
        + '<p><b>Calcule les probabilités demandées.</b> Donne des fractions irréductibles.</p>',
        { ...opts(L_LOTERIE), mode: 'saisie' });
    payload._meta.params = { N, a, b, c, d, perd };
    return payload;
};

// ═════════ « urne » ═════════

const COULEURS = ['blanches', 'noires', 'grises', 'rouges', 'vertes', 'bleues'];

const genUrneTrous = () => {
    const [c1, c2, c3] = shuffle(COULEURS).slice(0, 3);
    const n1 = randInt(3, 9); const n2 = randInt(3, 9); const n3 = randInt(3, 9);
    const T = n1 + n2 + n3;
    const phrase = `$P(\\text{${c2.slice(0, -1)}}) = ${trousFrac(n2, T)} \\qquad`
        + ` P(\\text{contraire}) = ${trousFrac(T - n2, T)}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Une urne contient ${n1} boules ${c1}, ${n2} boules ${c2} et ${n3} boules ${c3}, `
        + 'indiscernables au toucher. On tire une boule au hasard.</p>'
        + `<p><b>Calcule la probabilité de tirer une boule ${c2.slice(0, -1)}, puis celle de l'événement `
        + 'contraire.</b> Donne des fractions irréductibles.</p>',
        { ...opts(L_URNE), mode: 'saisie' });
    payload._meta.params = { c1, c2, c3, n1, n2, n3 };
    return payload;
};

// ═════════ « contraire » — QCM (phrase non négative) ═════════

const genContraireQcm = () => {
    const [c1, c2, c3] = shuffle(COULEURS).slice(0, 3);
    const sing = (c) => c.slice(0, -1);                    // « noires » → « noire »
    const correct = `Tirer une boule ${sing(c1)} ou une boule ${sing(c3)}.`;
    const choix = [correct,
        `Tirer une boule ${sing(c2)} ou une boule ${sing(c3)}.`,
        `Tirer une boule ${sing(c1)} ou une boule ${sing(c2)}.`,
        `Tirer une boule ${sing(c3)}.`];
    const payload = makeQcm(choix, correct,
        `<p>Une urne contient des boules ${c1}, ${c2} et ${c3}, au moins une de chaque.</p>`
        // « Formule-le sans négation » indiquait quelle forme de réponse
        // chercher parmi les choix — c'est ce que la carte évalue.
        + `<p><b>Quel est l'événement contraire de « tirer une boule ${sing(c2)} » ?</b></p>`,
        opts(L_CONTRAIRE));
    payload._meta.params = { c1, c2, c3 };
    return payload;
};

// ═════════ « tableau » — double entrée + conditionnelle en acte ═════════

const genTableauTrous = () => {
    const dpG = randInt(5, 12); const dpF = randInt(5, 12); const extF = randInt(2, 6); const extG = randInt(2, 6);
    const T = dpG + dpF + extF + extG;
    const filles = dpF + extF;
    const table = `<table style="border-collapse:collapse;margin:12px auto">`
        + `<tr><th style="${T_TH}"></th><th style="${T_TH}">Garçons</th><th style="${T_TH}">Filles</th></tr>`
        + `<tr><th style="${T_TH}">Demi-pensionnaires</th><td style="${T_TD}">${dpG}</td><td style="${T_TD}">${dpF}</td></tr>`
        + `<tr><th style="${T_TH}">Externes</th><td style="${T_TD}">?</td><td style="${T_TD}">${extF}</td></tr></table>`;
    const phrase = `$\\text{Externes garçons : } {{${extG}}} \\qquad P(\\text{fille}) = ${trousFrac(filles, T)}$\n`
        + `$\\text{Parmi les demi-pensionnaires : } P(\\text{garçon}) = ${trousFrac(dpG, dpG + dpF)}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Une classe compte ${T} élèves :</p>${table}`
        // « Attention à la dernière : on choisit parmi les demi-pensionnaires
        // SEULEMENT » désamorçait la probabilité conditionnelle ; la restriction
        // est désormais portée par la phrase à compléter, qui la dit sans
        // souligner que c'est un piège.
        + '<p><b>Complète la case manquante, puis calcule les probabilités.</b> '
        + 'Donne des fractions irréductibles.</p>',
        { ...opts(L_TABLEAU), mode: 'saisie' });
    payload._meta.params = { dpG, dpF, extF, extG, T };
    return payload;
};

// ═════════ « deuxepreuves » — roue à k secteurs, deux tours ═════════

const genDeuxEpreuvesTrous = () => {
    const k = randInt(2, 6);
    const g = k === 2 ? 1 : randInt(1, k - 1);             // g secteurs gagnants parmi k
    const support = randomChoice(['Une roue équilibrée', 'Une cible équilibrée']);
    const habillage = k === 2
        ? ['On lance deux fois une pièce équilibrée.', 'd\'obtenir PILE les deux fois', 'd\'obtenir au moins une fois FACE']
        : [`${support} comporte ${k} secteurs identiques, dont ${g === 1 ? 'UN seul est gagnant' : `${g} sont gagnants`}. On joue deux fois.`,
            'd\'obtenir un secteur gagnant les deux fois', 'd\'obtenir au moins une fois un secteur perdant'];
    const phrase = `$P(\\text{${k === 2 ? 'deux PILE' : 'deux fois gagnant'}}) = ${trousFrac(g * g, k * k)}`
        + ` \\qquad P(\\text{${k === 2 ? 'au moins une fois FACE' : 'au moins un secteur perdant'}})`
        + ` = ${trousFrac(k * k - g * g, k * k)}$`;
    const payload = makeTexteTrou(phrase,
        `<p>${habillage[0]}</p>`
        // « il y a k × k issues, dont g × g gagnantes » était le dénombrement,
        // c'est-à-dire les deux fractions attendues.
        + `<p><b>Calcule la probabilité ${habillage[1]}, puis, avec l'événement contraire, `
        + `celle ${habillage[2]}.</b> Donne des fractions irréductibles.</p>`,
        { ...opts(L_DEUXEPREUVES), mode: 'saisie' });
    payload._meta.params = { k, g };
    return payload;
};

// ═════════ « remise » — deux tirages avec remise ═════════

const genRemiseTrous = () => {
    const k = randInt(2, 5);
    const r = randInt(2, 6);
    const T = k * r;
    const autres = T - r;
    const [cR, cB] = shuffle(['rouges', 'bleues', 'vertes', 'jaunes']).slice(0, 2);
    const singR = cR.slice(0, -1);
    const phrase = `$P(\\text{${singR} au 1er tirage}) = ${trousFrac(r, T)} \\qquad`
        + ` P(\\text{${singR} au 2e tirage}) = ${trousFrac(r, T)}$\n`
        + `$P(\\text{deux ${cR}}) = ${trousFrac(1, k * k)} \\qquad`
        + ` P(\\text{au moins une ${singR}}) = ${trousFrac(2 * k - 1, k * k)}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Une urne contient ${r} boules ${cR} et ${autres} boules ${cB}, soit ${T} boules en tout. `
        + 'On tire une boule, on note sa couleur, on la REMET, puis on tire de nouveau.</p>'
        // « la remise fait que le 2e tirage a les mêmes probabilités que le
        // 1er » donnait le 2e trou à partir du 1er.
        + '<p><b>Complète.</b> Donne des fractions irréductibles.</p>',
        { ...opts(L_REMISE), mode: 'saisie' });
    payload._meta.params = { k, r, T };
    return payload;
};

// ═════════ « roues » — nombres à deux chiffres ═════════

const genRouesTrous = () => {
    let dizaines; let unites; let nombres; let seuilD; let favSup; let favDiv3;
    for (let essai = 0; essai < 80; essai++) {
        dizaines = shuffle([1, 2, 3, 4, 5]).slice(0, 4).sort((a, b) => a - b);
        unites = shuffle([2, 3, 5, 6, 7, 8, 9]).slice(0, 4).sort((a, b) => a - b);
        nombres = [];
        for (const d of dizaines) for (const u of unites) nombres.push(10 * d + u);
        seuilD = dizaines[randInt(1, 2)];
        favSup = nombres.filter(n => n > seuilD * 10 + 9).length;   // dizaines strictement supérieures
        favDiv3 = nombres.filter(n => n % 3 === 0).length;
        if (favSup >= 4 && favSup <= 12 && favDiv3 >= 2 && favDiv3 <= 10) break;
        favSup = 0;
    }
    if (!favSup) { dizaines = [1, 2, 3, 4]; unites = [6, 7, 8, 9]; nombres = []; for (const d of dizaines) for (const u of unites) nombres.push(10 * d + u); seuilD = 3; favSup = 4; favDiv3 = nombres.filter(n => n % 3 === 0).length; }
    const phrase = `$\\text{Nombre d'issues : } {{16}} \\qquad P(\\text{nombre} \\gt ${seuilD * 10 + 9})`
        + ` = ${trousFrac(favSup, 16)} \\qquad P(\\text{divisible par 3}) = ${trousFrac(favDiv3, 16)}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Une roue A, portant ${dizaines.join(', ')}, donne le chiffre des dizaines ; une roue B, `
        + `portant ${unites.join(', ')}, donne le chiffre des unités. On forme un nombre à deux chiffres.</p>`
        // « liste les nombres si besoin » suggérait la stratégie de dénombrement.
        + '<p><b>Compte les issues, puis calcule les probabilités.</b> Donne des fractions '
        + 'irréductibles.</p>',
        { ...opts(L_ROUES), mode: 'saisie' });
    payload._meta.params = { dizaines, unites, seuilD, favSup, favDiv3 };
    return payload;
};

// ═════════ « doubles » — deux urnes numérotées ═════════

const genDoublesTrous = () => {
    let B; let R; let communs;
    for (let essai = 0; essai < 60; essai++) {
        B = shuffle([1, 2, 3, 4, 5, 6]).slice(0, 3).sort((a, b) => a - b);
        R = shuffle([1, 2, 3, 4, 5, 6, 7]).slice(0, 4).sort((a, b) => a - b);
        communs = B.filter(x => R.includes(x)).length;
        if (communs >= 1 && communs <= 3) break;
        communs = 0;
    }
    if (!communs) { B = [2, 3, 4]; R = [2, 3, 4, 5]; communs = 3; } // repli (doc Ex.10)
    const nb = B.length * R.length;
    const phrase = `$\\text{Nombre de tirages possibles : } {{${nb}}} \\qquad`
        + ` P(\\text{double}) = ${trousFrac(communs, nb)}$`;
    const payload = makeTexteTrou(phrase,
        `<p>Une urne bleue contient des boules numérotées ${B.join(', ')} ; une urne rouge, des boules `
        + `numérotées ${R.join(', ')}. On tire une boule dans chaque urne : le tirage (3 ; 4) est différent `
        + 'de (4 ; 3). Un « double » est un tirage où les deux numéros sont égaux.</p>'
        + `<p><b>Compte les tirages possibles, puis calcule ${tex('P(\\text{double})')}.</b> `
        + 'Donne une fraction irréductible.</p>',
        { ...opts(L_DOUBLES), mode: 'saisie' });
    payload._meta.params = { B, R, communs, nb };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    loterie_texte_trou_saisie: genLoterieTrous,
    urne_texte_trou_saisie: genUrneTrous,
    contraire_qcm_unique: genContraireQcm,
    tableau_texte_trou_saisie: genTableauTrous,
    deuxepreuves_texte_trou_saisie: genDeuxEpreuvesTrous,
    remise_texte_trou_saisie: genRemiseTrous,
    roues_texte_trou_saisie: genRouesTrous,
    doubles_texte_trou_saisie: genDoublesTrous,
};

const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'loterie', label: L_LOTERIE, formatsList: buildFormats('loterie') },
    { id: 'urne', label: L_URNE, formatsList: buildFormats('urne') },
    { id: 'contraire', label: L_CONTRAIRE, formatsList: buildFormats('contraire') },
    { id: 'tableau', label: L_TABLEAU, formatsList: buildFormats('tableau') },
    { id: 'deuxepreuves', label: L_DEUXEPREUVES, formatsList: buildFormats('deuxepreuves') },
    { id: 'remise', label: L_REMISE, formatsList: buildFormats('remise') },
    { id: 'roues', label: L_ROUES, formatsList: buildFormats('roues') },
    { id: 'doubles', label: L_DOUBLES, formatsList: buildFormats('doubles') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
